import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAdmin } from "../context/AdminContext";
import {
    FaTachometerAlt, FaEnvelopeOpen, FaStar, FaLayerGroup,
    FaSignOutAlt, FaBars, FaTimes, FaGlobe,
} from "react-icons/fa";

const NAV = [
    { to: "/admin/dashboard", icon: FaTachometerAlt, label: "Dashboard" },
    { to: "/admin/enquiries", icon: FaEnvelopeOpen, label: "Enquiries" },
    { to: "/admin/testimonials", icon: FaStar, label: "Testimonials" },
    { to: "/admin/services", icon: FaLayerGroup, label: "Services" },
];

export default function AdminLayout({ children }) {
    const { admin, logout } = useAdmin();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="px-6 py-6 border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FaGlobe className="text-white text-lg" />
                    </div>
                    <div>
                        <p className="text-white font-bold text-sm leading-none">Lumis Admin</p>
                        <p className="text-slate-400 text-xs mt-0.5">Control Panel</p>
                    </div>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-4 py-6 space-y-1">
                {NAV.map(({ to, icon: Icon, label }) => {
                    const active = location.pathname === to;
                    return (
                        <Link
                            key={to}
                            to={to}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${active
                                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                : "text-slate-400 hover:text-white hover:bg-slate-700/60"
                                }`}
                        >
                            <Icon className="text-base flex-shrink-0" />
                            {label}
                        </Link>
                    );
                })}
            </nav>

            {/* User + Logout */}
            <div className="px-4 pb-6 border-t border-slate-700/50 pt-4">
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-700/40 mb-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">
                            {admin?.name?.[0]?.toUpperCase() || "A"}
                        </span>
                    </div>
                    <div className="min-w-0">
                        <p className="text-white text-xs font-semibold truncate">{admin?.name}</p>
                        <p className="text-slate-400 text-xs truncate">{admin?.email}</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition text-sm font-medium"
                >
                    <FaSignOutAlt />
                    Sign Out
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex h-screen bg-slate-100 font-sans">
            {/* ── Desktop sidebar ── */}
            <aside className="hidden lg:flex flex-col w-64 bg-slate-900 flex-shrink-0">
                <SidebarContent />
            </aside>

            {/* ── Mobile sidebar overlay ── */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-64 bg-slate-900 flex flex-col">
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-white"
                        >
                            <FaTimes size={18} />
                        </button>
                        <SidebarContent />
                    </aside>
                </div>
            )}

            {/* ── Main ── */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top bar */}
                <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4 flex-shrink-0">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="lg:hidden text-slate-500 hover:text-slate-900"
                    >
                        <FaBars size={20} />
                    </button>
                    <div className="flex-1" />
                    <Link
                        to="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-slate-500 hover:text-blue-600 transition flex items-center gap-1.5"
                    >
                        <FaGlobe size={12} /> View Website
                    </Link>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-y-auto p-6">
                    {children}
                </main>
            </div>
        </div>
    );
}
